'use client';

import { c as _c } from "react/compiler-runtime";
import { convictionFraction, useGetMarket } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { EntityTag } from '@/components/search/entity-tag';
import { RowAside, TickerText } from '@/components/search/search-row-parts';
import { tickerLabel } from '@/components/search/search-row-parts.utils';
import { SearchRowSubline } from '@/components/search/search-row-subline';
import type { OutcomeSearchResult } from '@/components/search/search-types';
import { useCommandLink } from '@/components/search/use-command-link';
import { CommandItem } from '@/components/ui/app-command';
import { formatPercent } from '@/lib/chart/format';
import { useFormattedCurrency } from '@/lib/currency';
interface OutcomeRowProps {
  outcome: OutcomeSearchResult;
  href: string;
  /** Highlight the ticker (used in `$ticker` search mode). */
  tickerHighlighted?: boolean;
  onCommit: () => void;
  onHoverPrefetch?: (marketPubkey: string) => void;
}

// The displayed outcome % is the normalized conviction share (~0→1) via the
// SDK's single `convictionFraction` — values sum to ~100% across outcomes and
// the market resolves when the leading share crosses θ (≤ 100%). Legacy
// gauge-era markets may transiently read > 100%; display must not clamp.
// `null` when the market has no conviction yet (pre-first-trade) so the row
// hides the slot rather than rendering "0.0%".
function convictionRatio(ppb: string | undefined): number | null {
  if (ppb === undefined || ppb === '') return null;
  const n = Number(ppb);
  if (!Number.isFinite(n) || n <= 0) return null;
  return convictionFraction(ppb);
}
function liveConvictionRatio(outcomes: readonly {
  readonly conviction: string;
}[] | undefined, index: number): number | null {
  return convictionRatio(outcomes?.[index]?.conviction);
}
function nonEmpty(value: string | undefined): string | null {
  if (value === undefined || value === '') return null;
  return value;
}
function shouldShowNumeric(value: string | undefined): boolean {
  if (value === undefined || value === '' || value === '0') return false;
  try {
    return BigInt(value) > BigInt(0);
  } catch {
    // SILENT-CATCH: non-numeric strings collapse the metric fragment
    return false;
  }
}
export function OutcomeRow(t0) {
  const $ = _c(53);
  const {
    outcome,
    href,
    tickerHighlighted,
    onCommit,
    onHoverPrefetch
  } = t0;
  const t = useTranslations("search");
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    onSelect,
    overlay
  } = useCommandLink(href, onCommit);
  let t1;
  if ($[0] !== outcome.market) {
    t1 = {
      pubkey: outcome.market
    };
    $[0] = outcome.market;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const live = useGetMarket(t1);
  let t2;
  if ($[2] !== live.data?.outcomes || $[3] !== outcome.convictionPpb || $[4] !== outcome.outcomeIndex) {
    const ratio = liveConvictionRatio(live.data?.outcomes, outcome.outcomeIndex) ?? convictionRatio(outcome.convictionPpb);
    t2 = ratio !== null ? formatPercent(ratio) : null;
    $[2] = live.data?.outcomes;
    $[3] = outcome.convictionPpb;
    $[4] = outcome.outcomeIndex;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const probabilityLabel = t2;
  let t3;
  if ($[6] !== onHoverPrefetch || $[7] !== outcome.market) {
    t3 = () => {
      onHoverPrefetch?.(outcome.market);
    };
    $[6] = onHoverPrefetch;
    $[7] = outcome.market;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleHover = t3;
  let t4;
  if ($[9] !== outcome.coverImageUrl) {
    t4 = nonEmpty(outcome.coverImageUrl);
    $[9] = outcome.coverImageUrl;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const usingCover = t4 !== null;
  let t5;
  if ($[11] !== outcome.coverImageUrl || $[12] !== outcome.marketImageUrl || $[13] !== usingCover) {
    t5 = usingCover ? outcome.coverImageUrl : nonEmpty(outcome.marketImageUrl);
    $[11] = outcome.coverImageUrl;
    $[12] = outcome.marketImageUrl;
    $[13] = usingCover;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const imageSrc = t5;
  const imageNsfw = usingCover ? outcome.coverImageNsfw ?? false : outcome.marketImageNsfw ?? false;
  let t6;
  if ($[15] !== outcome.marketTitle) {
    t6 = nonEmpty(outcome.marketTitle);
    $[15] = outcome.marketTitle;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const subtitle = t6;
  let t7;
  if ($[17] !== formatD6 || $[18] !== outcome.marketCapD6 || $[19] !== t) {
    t7 = shouldShowNumeric(outcome.marketCapD6) ? t("label_mkt_cap", {
      value: formatD6(outcome.marketCapD6, "compact")
    }) : t("label_chance");
    $[17] = formatD6;
    $[18] = outcome.marketCapD6;
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const mktCapLabel = t7;
  let t8;
  if ($[21] !== imageNsfw || $[22] !== imageSrc || $[23] !== outcome.market) {
    t8 = <OptimizedImage variant="thumbnail" size="size-8" entity={{
      kind: "market",
      pubkey: outcome.market,
      category: "",
      coverImageUrl: imageSrc,
      nsfw: imageNsfw
    }} alt="" preview={false} />;
    $[21] = imageNsfw;
    $[22] = imageSrc;
    $[23] = outcome.market;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== outcome.marketType || $[26] !== t8) {
    t9 = <ThumbWithOrigin marketType={outcome.marketType} size="sm">{t8}</ThumbWithOrigin>;
    $[25] = outcome.marketType;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== outcome.label) {
    t10 = <span className="truncate text-body font-medium">{outcome.label}</span>;
    $[28] = outcome.label;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <EntityTag kind="outcome" />;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10) {
    t12 = <div className="flex min-w-0 items-center gap-2">{t10}{t11}</div>;
    $[31] = t10;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== outcome.ticker || $[34] !== tickerHighlighted) {
    t13 = tickerLabel(outcome.ticker) !== null ? <TickerText key="ticker" ticker={outcome.ticker} highlighted={tickerHighlighted} /> : null;
    $[33] = outcome.ticker;
    $[34] = tickerHighlighted;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== subtitle || $[37] !== t13) {
    t14 = <SearchRowSubline pieces={[t13, subtitle]} />;
    $[36] = subtitle;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t12 || $[40] !== t14) {
    t15 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t12}{t14}</div>;
    $[39] = t12;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== mktCapLabel || $[43] !== probabilityLabel) {
    t16 = probabilityLabel !== null ? <RowAside value={probabilityLabel} label={mktCapLabel} mono={true} /> : null;
    $[42] = mktCapLabel;
    $[43] = probabilityLabel;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== handleHover || $[46] !== onSelect || $[47] !== outcome.pubkey || $[48] !== overlay || $[49] !== t15 || $[50] !== t16 || $[51] !== t9) {
    t17 = <CommandItem value={outcome.pubkey} onSelect={onSelect} onMouseEnter={handleHover} onFocus={handleHover} className="relative gap-3 rounded-overlay-item px-3 py-2.5">{overlay}{t9}{t15}{t16}</CommandItem>;
    $[45] = handleHover;
    $[46] = onSelect;
    $[47] = outcome.pubkey;
    $[48] = overlay;
    $[49] = t15;
    $[50] = t16;
    $[51] = t9;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  return t17;
}